import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  email,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  Factory as FactoryIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionTransform } from '../../hooks/useRolePermissionTransform';

// Validadores personalizados
const validateRequired = required('Este campo es obligatorio');
const validateEmail = email('Debe ser un email válido');
const validatePhone = (value: string) => {
  if (value && !/^[\d\s\-\+\(\)]+$/.test(value)) {
    return 'Formato de teléfono inválido';
  }
  return undefined;
};

// Componente de título personalizado
const CreateTitle = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    <FactoryIcon color='primary' sx={{ fontSize: 32 }} />
    <Typography variant='h4' component='h1' fontWeight='bold'>
      Crear Nueva Planta
    </Typography>
  </Box>
);

const PlantsCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { transformRoles } = useRolePermissionTransform();

  const handleSuccess = (data: any) => {
    notify('Planta creada exitosamente', { type: 'success' });
    redirect('show', 'plants', data.id);
  };

  const handleError = (error: any) => {
    console.error('Error creating plant:', error);
    notify('Error al crear la planta. Por favor intente nuevamente.', {
      type: 'error',
    });
  };

  return (
    <Create
      transform={transformRoles}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError,
      }}
      sx={{
        '& .RaCreate-card': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
        <CreateTitle />

        <SimpleForm
          sx={{
            '& .RaSimpleForm-toolbar': {
              marginTop: 3,
              padding: 2,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
            },
          }}
        >
          <Grid container spacing={3}>
            {/* Información Principal */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <FactoryIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Información de la Planta
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <TextInput
                    source='plantName'
                    label='Nombre de la Planta'
                    validate={validateRequired}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Ingrese el nombre identificativo de la planta'
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Información de Contacto */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PhoneIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Información de Contacto
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <TextInput
                    source='clientPhoneContact'
                    label='Teléfono de Contacto'
                    validate={validatePhone}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Número de teléfono para contacto'
                  />

                  <TextInput
                    source='clientEmail'
                    label='Email de Contacto'
                    validate={validateEmail}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Dirección de email para contacto'
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Cliente Asociado */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <BusinessIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Cliente Asociado
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <ReferenceInput
                    source='clientId'
                    reference='clients'
                    sort={{ field: 'clientName', order: 'ASC' }}
                  >
                    <SelectInput
                      optionText='clientName'
                      optionValue='id'
                      label='Cliente'
                      validate={validateRequired}
                      fullWidth
                      helperText='Seleccione el cliente propietario de esta planta'
                    />
                  </ReferenceInput>
                </CardContent>
              </Card>
            </Grid>

            {/* Componente Universal de Roles y Permisos */}
            <Grid item xs={12}>
              <RolePermissionManager
                title="Permisos de la Planta"
                subtitle="Configurar roles y permisos de acceso para esta planta"
                helperText="Los usuarios con diferentes roles tendrán diferentes niveles de acceso a la información y funcionalidades de esta planta."
              />
            </Grid>
          </Grid>
        </SimpleForm>
      </Box>
    </Create>
  );
};

export default PlantsCreate;
